import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { File, Plus, Search, Trash2, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { BACKEND_URL } from '@/lib/backend';
export default function DocumentsModule() {
  const [documents, setDocuments] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    organization_id: '',
    title: '',
    content: '',
    category: 'SOP',
    tags: '',
    is_public: false
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [docsRes, orgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/documentation/documents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/documentation/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (orgRes.ok) setOrganizations(await orgRes.json());
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        organization_id: formData.organization_id === 'global' ? '' : formData.organization_id,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      const response = await fetch(`${BACKEND_URL}/api/documentation/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setShowAddDialog(false);
        setFormData({ organization_id: '', title: '', content: '', category: 'SOP', tags: '', is_public: false });
        loadData();
      }
    } catch (err) {
      console.error('Failed to create:', err);
    }
  };

  const handleDelete = async (id, title) => {
    if (!confirm(`Delete document: ${title}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/documents/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filtered = documents.filter(d =>
    d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Documents & SOPs</h1>
          <p className="text-sm text-slate-500 mt-1">Standard Operating Procedures & Documentation</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />Add Document
          </Button>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>Add New Document</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Organization (Optional)</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Global or select organization" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (All Organizations)</SelectItem>
                    {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required /></div>
                <div><Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SOP">SOP</SelectItem>
                      <SelectItem value="Guide">Guide</SelectItem>
                      <SelectItem value="Policy">Policy</SelectItem>
                      <SelectItem value="Procedure">Procedure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Tags (comma-separated)</Label><Input value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})} placeholder="network, security, setup" /></div>
                <div className="col-span-2"><Label>Content *</Label><Textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} rows={10} required placeholder="Write your documentation here..." /></div>
                <div className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={formData.is_public} onChange={(e) => setFormData({...formData, is_public: e.target.checked})} className="w-4 h-4" /><Label>Make this document public to all organizations</Label></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">Create Document</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /><Input placeholder="Search documents..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></CardContent></Card>

      {loading ? <div className="text-center py-12"><p className="text-slate-500">Loading...</p></div> : filtered.length === 0 ? <Card><CardContent className="p-12 text-center"><FileText className="w-12 h-12 mx-auto text-slate-400 mb-3" /><p className="text-slate-600 font-medium">No documents found</p></CardContent></Card> : (
        <div className="space-y-3">
          {filtered.map((doc) => (
            <Card key={doc.id} className="hover:shadow-md transition-shadow border-l-4 border-l-indigo-500">
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-slate-800">{doc.title}</h3>
                      <Badge className="text-xs">{doc.category}</Badge>
                      {doc.is_public && <Badge variant="outline" className="text-xs">Public</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 mb-2 line-clamp-2">{doc.content}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      {doc.organization_name && <span>Org: {doc.organization_name}</span>}
                      <span>By: {doc.created_by_name}</span>
                      <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex gap-1">
                          {doc.tags.map((tag, i) => <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id, doc.title)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
